import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  getSaludDeNumeros,
  getProyeccionDeColas,
} from '../../../services/kapsoTemplatesService';

/**
 * Cuánto tardará en salir la cola de cada cliente.
 *
 * Va arriba y solo con lo que necesita atención: es un indicador **adelantado**,
 * no un informe. Un número nuevo empieza en el escalón más bajo de Meta y tarda
 * en calentarse, así que enterarse el día del desbordamiento llega tarde.
 */
const Proyeccion = ({ filas }) => {
  const avisos = (filas || []).filter(f => f.avisar);
  if (avisos.length === 0) return null;

  return (
    <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
      <h3 className='text-sm font-semibold text-amber-900'>
        {avisos.length === 1
          ? 'Un cliente no va a poder con su cola'
          : `${avisos.length} clientes no van a poder con su cola`}
      </h3>
      <ul className='mt-2 space-y-1.5'>
        {avisos.map(f => (
          <li key={f.company_id} className='text-sm text-amber-900'>
            <strong>{f.company_name}</strong>: {f.pendientes} pendientes ·{' '}
            {f.capacidad_diaria}/día con {f.numeros} número{f.numeros > 1 ? 's' : ''} ·{' '}
            <strong>tardará {f.dias_estimados} días</strong>
            {f.numeros_sugeridos > f.numeros && (
              <>
                {' '}
                · con {f.numeros_sugeridos} números bajaría del umbral
              </>
            )}
          </li>
        ))}
      </ul>
      <p className='mt-3 text-xs text-amber-800'>
        Un número nuevo <strong>no sirve el día que se abre</strong>: empieza en el escalón más
        bajo de Meta y sube con el tiempo y con buena calidad. Ábrelo con antelación, no cuando
        haga falta.
      </p>
    </div>
  );
};

/**
 * Estado de los números conectados, de todas las empresas a la vez.
 *
 * Se enseñan todos juntos a propósito: una pantalla por empresa obliga a ir una
 * por una a ver si alguna va mal, que es justo lo que nadie hace. Aquí el que
 * se está quemando sale solo.
 */

/**
 * Cómo se traduce lo que dice Meta.
 *
 * Verificado contra un número real el 04/09/2026: devolvió `CONNECTED`,
 * `UNKNOWN` y `TIER_250`. El resto de valores siguen sin confirmarse, así que
 * la regla se mantiene — **lo que no reconocemos se enseña como lo que es**, en
 * crudo y avisando, en vez de traducirse a un color inventado.
 */
const CALIDAD = {
  GREEN: ['Alta', 'bg-emerald-100 text-emerald-700', 0],
  YELLOW: ['Media', 'bg-amber-100 text-amber-700', 1],
  RED: ['Baja', 'bg-red-100 text-red-700', 2],
  // Verificado contra un número real el 04/09/2026: Meta devuelve `UNKNOWN` en
  // un número recién conectado. No es una anomalía ni un valor que no
  // reconozcamos — es «todavía no ha mandado suficiente para calificarlo». Sale
  // en neutro y NO cuenta como riesgo, que es lo que era antes de tener el
  // valor real: se pintaba crudo y ordenaba como si fuera media.
  UNKNOWN: ['Sin calificar', 'bg-slate-100 text-slate-600', 0],
};
const SITUACION = {
  CONNECTED: ['Conectado', 'bg-emerald-100 text-emerald-700', 0],
  FLAGGED: ['Marcado por Meta', 'bg-amber-100 text-amber-700', 2],
  RESTRICTED: ['Restringido', 'bg-red-100 text-red-700', 3],
  BANNED: ['Baneado', 'bg-red-100 text-red-700', 4],
};

const Valor = ({ valor, mapa }) => {
  if (!valor) {
    return <span className='text-xs text-slate-400'>sin datos</span>;
  }
  const conocido = mapa[valor];
  return (
    <span
      className={`px-2 py-0.5 rounded-lg text-[11px] font-medium whitespace-nowrap ${
        conocido ? conocido[1] : 'bg-slate-100 text-slate-600'
      }`}
      title={conocido ? valor : `Valor que no reconocemos: ${valor}`}
    >
      {conocido ? conocido[0] : valor}
    </span>
  );
};

/** El cambio es la señal, así que se enseña al lado del valor. */
const Cambio = ({ previo, cuando }) => {
  if (!previo) return null;
  const fecha = cuando ? new Date(cuando).toLocaleDateString('es-ES') : null;
  return (
    <span className='block text-[11px] text-slate-500 mt-0.5'>
      antes {previo}
      {fecha && ` · ${fecha}`}
    </span>
  );
};

/**
 * Lo peor arriba: es lo que hay que mirar primero.
 *
 * Un número al que hemos perdido el acceso va por delante de todo lo demás, y
 * por eso suma 100: da igual lo buena que fuera su calidad: no se puede enviar.
 * Antes ni siquiera aparecía en la lista.
 */
const riesgo = n =>
  (caido(n) ? 100 : 0) +
  (SITUACION[n.meta_status]?.[2] ?? 1) * 10 +
  (CALIDAD[n.quality_rating]?.[2] ?? 1);

/** ¿Este número está fuera de juego, por lo que sea? */
const caido = n => n.connection_status === 'disconnected' || n.meta_access === 'lost';

const KapsoPhoneHealth = () => {
  const [numeros, setNumeros] = useState(null);
  const [proyeccion, setProyeccion] = useState(null);
  const [error, setError] = useState(null);
  const [empresa, setEmpresa] = useState('');
  const [situacion, setSituacion] = useState('');
  const [orden, setOrden] = useState('riesgo');

  const cargar = useCallback(async () => {
    try {
      const [salud, colas] = await Promise.all([getSaludDeNumeros(), getProyeccionDeColas()]);
      setNumeros(salud);
      setProyeccion(colas);
      setError(null);
    } catch (err) {
      setError(err.message || 'No se ha podido cargar el estado de los números');
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const empresas = useMemo(
    () => [...new Map((numeros || []).map(n => [n.company_id, n.company_name])).entries()],
    [numeros]
  );

  const visibles = useMemo(() => {
    let lista = [...(numeros || [])];
    if (empresa) lista = lista.filter(n => n.company_id === empresa);
    if (situacion) lista = lista.filter(n => (n.meta_status || 'sin_datos') === situacion);

    const por = {
      riesgo: (a, b) => riesgo(b) - riesgo(a),
      empresa: (a, b) => (a.company_name || '').localeCompare(b.company_name || ''),
      calidad: (a, b) => (CALIDAD[b.quality_rating]?.[2] ?? 1) - (CALIDAD[a.quality_rating]?.[2] ?? 1),
    };
    return lista.sort(por[orden] || por.riesgo);
  }, [numeros, empresa, situacion, orden]);

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 p-4 text-sm text-red-700'>
        {error}{' '}
        <button type='button' onClick={cargar} className='underline font-medium'>
          Reintentar
        </button>
      </div>
    );
  }

  if (!numeros) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-400'>
        Cargando…
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <Proyeccion filas={proyeccion} />

      <p className="text-sm text-slate-500">
        Lo que Meta dice de cada número conectado. <strong>Lo peor sale arriba.</strong> Cuando un
        valor cambia se guarda el anterior: eso es lo que avisa antes de que restrinjan un número.
      </p>

      {/* El aviso va arriba y solo cuando hay alguno: un número sin acceso no
          es un matiz de su salud, es que ese cliente no puede enviar nada. */}
      {visibles.some(caido) && (
        <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900'>
          <strong>
            {visibles.filter(caido).length === 1
              ? 'Hay un número sin acceso'
              : `Hay ${visibles.filter(caido).length} números sin acceso`}
          </strong>
          : Meta ya no los reconoce, así que por ahí{' '}
          <strong>no sale ni un mensaje</strong>. El cliente tiene que volver a conectarlo.
          <p className='mt-2 text-xs text-red-800'>
            Meta desconecta sola una cuenta que lleva semanas sin enviar nada. Pasa sobre todo
            durante el alta, mientras se espera a que aprueben la primera plantilla.
          </p>
        </div>
      )}

      <div className='bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-4'>
        <div>
          <label htmlFor='salud-empresa' className='block text-xs font-medium text-slate-600 mb-1'>
            Empresa
          </label>
          <select
            id='salud-empresa'
            value={empresa}
            onChange={e => setEmpresa(e.target.value)}
            className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
          >
            <option value=''>Todas</option>
            {empresas.map(([id, nombre]) => (
              <option key={id} value={id}>
                {nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor='salud-situacion' className='block text-xs font-medium text-slate-600 mb-1'>
            Situación
          </label>
          <select
            id='salud-situacion'
            value={situacion}
            onChange={e => setSituacion(e.target.value)}
            className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
          >
            <option value=''>Todas</option>
            {Object.entries(SITUACION).map(([valor, [etiqueta]]) => (
              <option key={valor} value={valor}>
                {etiqueta}
              </option>
            ))}
            <option value='sin_datos'>Sin datos</option>
          </select>
        </div>

        <div>
          <label htmlFor='salud-orden' className='block text-xs font-medium text-slate-600 mb-1'>
            Ordenar por
          </label>
          <select
            id='salud-orden'
            value={orden}
            onChange={e => setOrden(e.target.value)}
            className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
          >
            <option value='riesgo'>Riesgo</option>
            <option value='empresa'>Empresa</option>
            <option value='calidad'>Calidad</option>
          </select>
        </div>
      </div>

      {visibles.length === 0 ? (
        <div className='bg-white rounded-lg border border-slate-200 p-4 text-sm text-slate-500'>
          {numeros.length === 0
            ? 'Todavía no hay ningún número conectado.'
            : 'Ningún número cumple ese filtro.'}
        </div>
      ) : (
        <div className='bg-white rounded-lg border border-slate-200 overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-slate-200 text-left'>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Empresa</th>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Número</th>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Situación</th>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Calidad</th>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Cupo de Meta</th>
                <th className='px-3 py-3 text-xs font-medium text-slate-500'>Últ. revisión</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(n => (
                <tr
                  key={n.phone_number_id}
                  className={`border-b border-slate-100 last:border-b-0 ${
                    caido(n) ? 'bg-red-50' : ''
                  }`}
                >
                  <td className='px-3 py-3 text-slate-900'>{n.company_name}</td>
                  <td className='px-3 py-3'>
                    <span className='text-slate-700 whitespace-nowrap'>{n.phone_number || n.name || '—'}</span>
                    {n.is_coexistence && (
                      <span className='block text-[11px] text-slate-400'>comparte con el móvil</span>
                    )}
                  </td>
                  <td className='px-3 py-3'>
                    {/* Un número caído no enseña su «situación» de Meta: ese dato
                        es de la última vez que pudimos verlo, y pintarlo como si
                        fuera de ahora es justo lo que hizo que una desconexión
                        pasara nueve días inadvertida. */}
                    {caido(n) ? (
                      <>
                        <span className='px-2 py-0.5 rounded-lg text-[11px] font-medium whitespace-nowrap bg-red-100 text-red-700'>
                          Sin acceso
                        </span>
                        <span className='block text-[11px] text-red-700 mt-1 max-w-xs'>
                          {n.disconnected_reason ||
                            n.meta_access_error ||
                            'Meta ya no reconoce este número'}
                        </span>
                        {n.disconnected_at && (
                          <span className='block text-[11px] text-slate-500 mt-0.5'>
                            desde el {new Date(n.disconnected_at).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <Valor valor={n.meta_status} mapa={SITUACION} />
                        <Cambio previo={n.meta_status_previous} cuando={n.meta_status_changed_at} />
                      </>
                    )}
                  </td>
                  {/* Con el acceso perdido, calidad y cupo son el último dato que
                      llegamos a ver, no el de hoy. Se atenúan para que no se lean
                      como vigentes. */}
                  <td className={`px-3 py-3 ${caido(n) ? 'opacity-40' : ''}`}>
                    <Valor valor={n.quality_rating} mapa={CALIDAD} />
                    <Cambio
                      previo={n.quality_rating_previous}
                      cuando={n.quality_rating_changed_at}
                    />
                  </td>
                  <td
                    className={`px-3 py-3 text-slate-600 whitespace-nowrap ${
                      caido(n) ? 'opacity-40' : ''
                    }`}
                  >
                    {n.messaging_limit || <span className='text-slate-400'>sin datos</span>}
                  </td>
                  <td className='px-3 py-3 text-xs text-slate-500'>
                    {n.last_checked_at
                      ? new Date(n.last_checked_at).toLocaleDateString('es-ES')
                      : 'nunca'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default KapsoPhoneHealth;
